import { Injectable, Injector, ViewContainerRef } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  map,
  Observable,
  tap,
} from 'rxjs';
import { environment } from '../../../../../environments/environment.development';
import { ErrorHandlingService } from '../../../../core/services/error-handling.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  CaseFeedBackReportResponse,
  FeedbackApiResponsePaginated,
  FeedBackByCase,
  FeedBackListParams,
  FeedbackQuestion,
  FeedbackResponse,
  FeedbackResponseDetail,
  FeedbackSubmission,
  Question,
  QuestionsResponse,
} from './feedback';
import { ApiResponse } from '../../../../core/models/api-response.models';
import { FrequencyDatasheetGraphComponent } from '../insights-data/data-graph/frequency-datasheet-graph/frequency-datasheet-graph.component';
import { FrequencyIntervalGraphComponent } from '../insights-data/data-graph/frequency-interval-graph/frequency-interval-graph.component';
import { LatencyGraphComponent } from '../insights-data/data-graph/latency-graph/latency-graph.component';
import { PartialIntervalGraphComponent } from '../insights-data/data-graph/partial-interval-graph/partial-interval-graph.component';
import { ReplacementBehaviorGraphComponent } from '../insights-data/data-graph/replacement-behavior-graph/replacement-behavior-graph.component';
import {
  frequencyDatasheetGraph,
  frequencyIntervalGraph,
  latencyGraph,
  partialIntervalGraph,
  replacementDataType,
} from '../insights-data/insights-data.interface';

@Injectable({
  providedIn: 'root',
})
export class FeedbackService {
  private readonly baseUrl = environment.apiUrl;
  private readonly API_URL = `${environment.apiUrl}/casefeedback`;

  private questionsSubject = new BehaviorSubject<QuestionsResponse | null>(
    null
  );

  readonly questions$ = this.questionsSubject.asObservable();

  private feedBackSubject =
    new BehaviorSubject<FeedbackApiResponsePaginated | null>(null);
  feedback$ = this.feedBackSubject.asObservable();
  private feedbackSubmissionState = new BehaviorSubject<boolean>(false);
  feedbackSubmissionState$ = this.feedbackSubmissionState
    .asObservable()
    .pipe(distinctUntilChanged());

  constructor(
    private errorHandler: ErrorHandlingService,
    private http: HttpClient,
    private injector: Injector
  ) {}

  getQuestionsList(): Observable<QuestionsResponse> {
    return this.http.get<QuestionsResponse>(`${this.API_URL}/list`).pipe(
      tap(response => this.questionsSubject.next(response)),
      catchError(error => this.errorHandler.handleError(error))
    );
  }
  getFeedbackQuestionsList(): Observable<ApiResponse<FeedbackQuestion[]>> {
    return this.http
      .get<
        ApiResponse<FeedbackQuestion[]>
      >(`${this.baseUrl}/organizationcasefeedback/get-questions`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  private buildQueryParams({
    PageSize,
    Page,
    SortOrder,
    Sort,
    caseId,
  }: FeedBackListParams): HttpParams {
    let params = new HttpParams()
      .set('Page', Page.toString())
      .set('PageSize', PageSize.toString());

    if (SortOrder) {
      params = params.set('SortOrder', SortOrder);
    }

    if (Sort) {
      params = params.set('Sort', Sort);
    }
    if (caseId) {
      params = params.set('caseId', caseId.toString());
    }
    return params;
  }
  getFeedBackList(
    params: FeedBackListParams
  ): Observable<FeedbackApiResponsePaginated> {
    const queryParams = this.buildQueryParams(params);
    return this.http
      .get<FeedbackApiResponsePaginated>(
        `${this.baseUrl}/organizationcasefeedback/case/get-feedback-submission`,
        {
          params: queryParams,
        }
      )
      .pipe(
        tap(response => this.feedBackSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  submitQuestionFeedback(
    submission: FeedBackByCase
  ): Observable<ApiResponse<FeedbackResponseDetail>> {
    return this.http
      .post<
        ApiResponse<FeedbackResponseDetail>
      >(`${this.baseUrl}/organizationcasefeedback/submit-feedback`, submission)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  submitFeedback(submission: FeedbackSubmission): Observable<FeedbackResponse> {
    return this.http
      .post<FeedbackResponse>(`${this.API_URL}/create`, submission)
      .pipe(
        map(response => response),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getFeedbackByCaseId(id: number): Observable<ApiResponse<FeedBackByCase>> {
    const params = new HttpParams().set(
      'caseFeedbackSubmissionId',
      id.toString()
    );

    return this.http
      .get<
        ApiResponse<FeedBackByCase>
      >(`${this.baseUrl}/organizationcasefeedback/get-feedback-submission`, { params })
      .pipe(
        map(response => response),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  mapQuestionTypes(questions: Question[]): Question[] {
    return questions.map(q => {
      const question = { ...q };

      if (
        q.questionText
          .toLowerCase()
          .includes('ratings based on the performance')
      ) {
        question.controlType = 'rating';
      } else if (
        q.questionText.toLowerCase().includes('describe modifications')
      ) {
        question.controlType = 'textarea';
      } else if (
        q.questionText.toLowerCase().includes('has the student') ||
        q.questionText.toLowerCase().includes('is the student') ||
        q.questionText.toLowerCase().includes('are there any')
      ) {
        question.controlType = 'radio';
      }

      return question;
    });
  }

  updateFeedbackCaseFlag(caseId: number): Observable<ApiResponse<boolean>> {
    return this.http
      .post<ApiResponse<boolean>>(
        `${this.API_URL}/update-feedback-case-flag`,
        null,
        {
          params: { caseId: caseId.toString() },
        }
      )
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  checkFeedbackSubmitted(caseId: number): Observable<boolean> {
    return this.http
      .get<ApiResponse<boolean>>(`${this.API_URL}/is-feedback-submitted`, {
        params: { caseId: caseId.toString() },
      })
      .pipe(
        map(response => response.result ?? false),
        tap(isSubmitted => {
          this.feedbackSubmissionState.next(isSubmitted);
        }),
        catchError(error => {
          this.feedbackSubmissionState.next(false);
          return this.errorHandler.handleError(error);
        })
      );
  }

  getCurrentFeedbackState(): boolean {
    return this.feedbackSubmissionState.getValue();
  }

  getCaseFeedbackReportData(
    caseFeedbackSubmissionId: number,
    organizationId: number
  ): Observable<ApiResponse<CaseFeedBackReportResponse>> {
    const params = new HttpParams()
      .set('caseFeedbackSubmissionId', caseFeedbackSubmissionId.toString())
      .set('organizationId', organizationId.toString());

    return this.http
      .get<ApiResponse<CaseFeedBackReportResponse>>(
        `${this.baseUrl}/organizationcasefeedback/get-case-feedback-report`,
        {
          params,
        }
      )
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
  // Keep type any for testing purpose, once type will defined from backend will updated. this feature is in progress and not consider in milestone 1.
  generateGraphImage(
    viewContainerRef: ViewContainerRef,
    selectedUnit: number,
    graphData:
      | frequencyDatasheetGraph
      | latencyGraph
      | partialIntervalGraph
      | replacementDataType
      | frequencyIntervalGraph,
    showIntervalFrequency: boolean
  ): Promise<string[]> {
    return new Promise(resolve => {
      let componentFactory: import('@angular/core').Type<
        | FrequencyDatasheetGraphComponent
        | FrequencyIntervalGraphComponent
        | LatencyGraphComponent
        | PartialIntervalGraphComponent
        | ReplacementBehaviorGraphComponent
      >;
      switch (selectedUnit) {
        case 1:
          componentFactory = showIntervalFrequency
            ? FrequencyIntervalGraphComponent
            : FrequencyDatasheetGraphComponent;
          break;
        case 2:
          componentFactory = LatencyGraphComponent;
          break;
        case 3:
          componentFactory = PartialIntervalGraphComponent;
          break;
        case 4:
          componentFactory = ReplacementBehaviorGraphComponent;
          break;
        default:
          resolve([]);
          return;
      }

      const factory = viewContainerRef.createComponent(componentFactory, {
        injector: this.injector,
      });

      const componentElement = factory.location.nativeElement;
      componentElement.style.display = 'none';
      (
        factory.instance as
          | FrequencyDatasheetGraphComponent
          | FrequencyIntervalGraphComponent
          | LatencyGraphComponent
          | PartialIntervalGraphComponent
          | ReplacementBehaviorGraphComponent
      ).graphData = graphData;

      let images: string[] = [];
      let imagesResolved = 0;
      const totalImages = selectedUnit === 3 ? 2 : 1;

      const checkResolve = () => {
        imagesResolved++;
        if (imagesResolved === totalImages) {
          resolve(images);
          factory.destroy();
        }
      };

      if (selectedUnit === 3) {
        (
          factory.instance as PartialIntervalGraphComponent
        ).allChartTotalImage.subscribe((imgArr: string[]) => {
          images = images.concat(imgArr);
          checkResolve();
        });
      }

      (
        factory.instance as
          | FrequencyDatasheetGraphComponent
          | FrequencyIntervalGraphComponent
          | LatencyGraphComponent
          | PartialIntervalGraphComponent
          | ReplacementBehaviorGraphComponent
      ).allChartImage.subscribe((imgArr: string[]) => {
        images = images.concat(imgArr);
        checkResolve();
      });
    });
  }
}
